import { FileText } from 'lucide-react-native';
import { Pressable } from 'react-native';

import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import {
  selectHeaderCaseLine,
  useClinicalCaseHeaderStore,
} from '@/services/chat/clinicalCaseHeader';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * The phone's way back to the case once its box has scrolled away, which on a long first message
 * is the moment the chat opens. Web shows the case line in its header instead, but the native bar
 * already holds back, the participant and the "…" menu, and iOS drops items that do not fit, so
 * the case rides under the bar as a small pill.
 *
 * It sits over the list rather than above it, so the list stays the first thing under the header.
 * Tapping opens the full summary as a peek, which carries the way to the message itself.
 */
export function ClinicalCaseJumpChip({
  conversationId,
  topInset,
}: {
  conversationId: string;
  topInset: number;
}) {
  const header = useClinicalCaseHeaderStore((state) => state.header);

  const peekId = useClinicalCaseHeaderStore((state) => state.peekId);

  const setPeekId = useClinicalCaseHeaderStore((state) => state.setPeekId);

  const line = selectHeaderCaseLine(header, conversationId);

  if (!line || peekId === conversationId) {
    return null;
  }

  return (
    <Box
      className="absolute inset-x-0 z-10 items-center"
      style={{ top: topInset + 8 }}
      pointerEvents="box-none">
      <Pressable
        onPress={() => setPeekId(conversationId)}
        className="h-12 justify-center"
        testID="clinical-case-jump-chip"
        {...getAccessibilityProps({
          label: `Case summary: ${line}`,
          hint: 'Shows the full summary',
          role: 'button',
        })}>
        <HStack className="items-center gap-1.5 rounded-full border border-outline-200 bg-background-0 px-3 py-2 shadow-soft-2">
          <Icon as={FileText} size="xs" className="text-typography-700" />
          <Text size="sm" className="font-medium text-typography-900">
            Case summary
          </Text>
        </HStack>
      </Pressable>
    </Box>
  );
}
